export interface HelpTextConfig {
    title: string;
    content: string;
    examples?: {
      good?: string[];
      bad?: string[];
      plainText?: string[];
    };
    notes?: string[];
    sections?: {
      title: string;
      content?: string;
      items?: string[];
    }[];
  }
  
  export const skillFormHelpText: Record<string, HelpTextConfig> = {
    skillName: {
      title: "Skill Name",
      content: "Choose a descriptive name that clearly indicates the skill's purpose. This name will be passed to AI and will help agents identify its purpose.",
      notes: [
        "Use clear, descriptive names.",
        "Keep it concise but meaningful.",
        "Include the name of the product or service where possible.",
        "Use snake_case for the skill names.",
        "Avoid uncommon abbreviations and generic names."
      ],
      examples: {
        good: [
          "roll_dice",
          "extend_kayako_license_expiry",
          "email_3rd_party"
        ],
        bad: [
          "roll-dice",
          "ExtendExpiry",
          "function_test"
        ]
      }
    },
    
    skillDescription: {
      title: "Skill Description",
      content: "Add a skill description that provides a clear overview of its functionality and usage details. This will be used by AI to better understand the skill's usage and expected outcomes.",
      notes: [
        "Keep it concise but meaningful.",
        "Do not include irrelevant details about the underlying code.",
        "Do not detail the input and output parameters; this will be done later."
      ],
      examples: {
        good: [
          "Rolls the specified number of 20-sided dice.",
          "Extends the license for the provided Kayako instance by the specified number of days.",
          "Sends an email to the specified addresses."
        ],
        bad: [
          "A skill that rolls dice when asked to do so",
          "Rolls dice",
          "return string will be int"
        ]
      }
    },

    skillTags: {
      title: "Skill Tags",
      content: "Add relevant tags to help categorize and find your skill within the marketplace.",
      notes: [
        "Use descriptive keywords.",
        "Add relevant categories.",
        "Include the product where applicable."
      ]
    },
  
    skillUrlEndpoint: {
      title: "URL Endpoint",
      content: "Provide the API endpoint URL where your skill is hosted. This endpoint will receive HTTPS POST requests whenever the skill is executed.",
      notes: [
        "Must use HTTPS protocol and support POST requests.",
        "Data will be supplied as parameters in the body.",
        "Authentication should be handled via the \"authorization\" header.",
        "When the endpoint is called with the URL parameter \"test_mode=true\", the endpoint should only execute read actions and provide mock data for write actions."
      ],
      examples: {
        good: [
          "https://api.company.com/v1/process",
          "https://skills-api.service.com/v2/analyze",
          "https://internal.org/api/skills/execute"
        ]
      }
    },

    skillAuthentication: {
      title: "Skill Authentication",
      content: "Specify the token to be supplied in the \"authorization\" header. This token is securely stored and will be retrieved during runtime when invoking the skill.",
      notes: [
        "The token must be a single string.",
        "The receiving endpoint will need to handle this string in its \"authorization\" header.",
        "Use a unique token for Agentic Genie, and do not share it with anyone.",
        "If you need to call your endpoint from a different source, consider implementing separate tokens or authentication methods.",
        "To prevent the \"authorization\" header from being sent, simply unselect the checkbox."
      ]
    },

    skillTimeout: {
      title: "Skill Timeout",
      content: "Specify the timeout in seconds for this skill's execution. If the endpoint takes longer than this to respond, the system will consider it to have timed out.",
    },
  
    skillInputParameters: {
      title: "Input Parameters",
      content: "Define the parameters that your skill accepts as input. These will be supplied in the body of the POST request.",
      notes: [
        "Use descriptive parameter names to ensure that the purpose of each is well understood.",
        "Provide good explanations about usage. If it would confuse a human, it is unlikely that AI will be able to use the parameters effectively."
      ],
      sections: [
        {
          title: "Parameter Structure",
          items: [
            "Key - Unique identifier for the parameter",
            "Description - Clear explanation of the parameter's purpose",
            "Type - Data type of the parameter",
            "Options (Optional) - Possible values or constraints",
            "Example (Optional) - Sample valid input"
          ]
        }
      ]
    },
  
    skillOutput: {
      title: "Output",
      content: "Define the expected output that your skill will return.",
      notes: [
        "Ensure that the output is clear and easy to understand.",
        "Contemplate all possible issues and return descriptive error messages. AI will need to understand whether it called a skill incorrectly, there's an internal issue, or if a process did not start."
      ],
      sections: [
        {
          title: "Output Structure",
          items: [
            "Description - Clear explanation of the returned value.",
            "Type - Data type of the output."
          ]
        }
      ]
    },
  
    skillAnnotation: {
      title: "Annotation",
      content: "Generate or edit the annotation for this skill. This will be used by AI to understand the skill's purpose and expected input and output.",
      notes: [
        "Fill in all other fields before generating the annotation.",
        "Consider manual edits to fine-tune the resulting annotation.",
        "Whenever you make a change to the skill, remember to update the annotation accordingly."
      ]
    }

  };

  export const agentFormHelpText: Record<string, HelpTextConfig> = {
    agentName: {
      title: "Agent Name",
      content: "Choose a unique and descriptive name for your agent. This will be used by AI to better understand its role.",
      notes: [
        "Use clear, descriptive names.",
        "Avoid generic names.",
        "Use CamelCase for the agent's name.",
        "Avoid special characters, numbers, or spaces."
      ],
      examples: {
        good: [
          "CustomerSupportAgent",
          "OrderProcessingBot",
          "SalesAssistant"
        ],
        bad: [
          "MyAgent",
          "Test2",
          "new bot"
        ]
      }
    },
    
    agentDescription: {
      title: "Agent Description",
      content: "Provide a detailed description of your agent's purpose and capabilities. This will be used by AI to identify the agent's capabilities and the role it should play.",
      notes: [
        "Provide information about the agent's role and capabilities.",
        "Avoid detailing the assigned skills, as this information will be fetched by AI where required.",
        "Consider explaining when the agent should be consulted or invoked."
      ],
      examples: {
        good: [
          "The agent responsible for executing tasks related to Jira. It can access and update Jira tickets and user information. If a linked Jira is present, it should always be consulted to fetch the required information."
        ]
      }
    },

    agentTags: {
      title: "Agent Tags",
      content: "Add relevant tags to help categorize and find your agent within the marketplace.",
      notes: [
        "Use descriptive keywords.",
        "Add relevant categories.",
        "Include the product where applicable."
      ]
    },
  
    agentPrompt: {
      title: "Agent Prompt",
      content: "Define the system prompt for the agent. This will control its behavior within the agent swarm.",
      notes: [
        "Use the usual prompting strategies.",
        "You do not need to explain all its skills to the agent; however, you may want to add special instructions for specific skills or scenarios.",
        "Note that this prompt is only visible to the agent itself."
      ]
    },

    agentSelectedSkills: {
      title: "Skills",
      content: "Select the skills that your agent will use to perform its tasks and provide responses. The agent will be aware of these skills' annotations and be able to use them accordingly."
    }
  };

  export const packageFormHelpText: Record<string, HelpTextConfig> = {
    packageName: {
      title: "Package Name",
      content: "Choose a unique and descriptive name for your package. This name will be used to launch the package, but will not be known by AI.",
      notes: [
        "Use clear, descriptive names, including the platform or product.",
        "Keep it concise but meaningful.",
        "Use CamelCase where possible.",
        "Avoid special characters."
      ],
      examples: {
        good: [
          "KayakoLicenseRenewal",
          "ACRMInvestigation",
          "JiveHostedRollingRestart"
        ],
        bad: [
          "Renewal",
          "RestartPackage",
          "NewPkg"
        ]
      }
    },
  
    packageProductName: {
      title: "Package Product Name",
      content: "Select the product name for the package, or add a new product where required.",
    },
  
    packageDescription: {
      title: "Package Description",
      content: "Provide an internal description of what the package does. This is just for human reference and will not be known by AI.",
      notes: [
        "Detail the expected use case for this package.",
        "Explain what the package can and cannot do.",
        "Note any prerequisites or limitations."
      ],
      examples: {
        good: [
          "A package to handle all requests related to license renewal queries for the Kayako product. It can escalate for approval but will andon cord for cancellation requests."
        ]
      }
    },
  
    packageTags: {
      title: "Package Tags",
      content: "Add relevant tags to help categorize and find your package within the marketplace.",
      notes: [
        "Use descriptive keywords.",
        "Add relevant categories.",
        "Include the product where applicable."
      ]
    },
  
    packageWorkflow: {
      title: "Package Workflow",
      content: "Detail, step by step, the task that the agent swarm will need to carry out.",
      notes: [
        "For workflows, use a clear step-by-step structure, detailing when steps need to be skipped.",
        "For open-ended tasks or investigations, consider the required steps and passing criteria.",
        "To obtain deterministic outcomes, use a high level of detail in the requests.",
        "Make sure that you know how the system operates to be able to properly define exit points. For example, sending a PR is generally the last step of a procedure before resolving or setting a ticket on-hold."
      ],
      examples: {
        plainText: [
`1. To start the process, make sure you have the tenant key, or retrieve it using your agents and tools based on the data you have if it was not provided.
  - To find the tenant key, you should use the the company name shared by the requester, their email domain, or the website URL. You have agents that have tools to find the tenant key using these information.
  - If you cannot find this key, you should get back to the customer to request it and finish the process.
2. Get the information for the specific tenant key.
  - If there's no active license, let the customer know that the key you found is expired, sharing the website and key number, and finish the process.
3. Determine if the requester is internal or external:
  - If the requester is internal, the request can be considered approved, and you can skip to STEP 9.
  - If the requester is external, proceed to STEP 4.
4. EXTERNAL ONLY: Identify the Account Manager.
5. EXTERNAL ONLY: Check whether a side conversation was conducted with that Account Manager.
6. EXTERNAL ONLY: If STEP 5 is True, check whether the Account Manager replied that side conversation or not.
7. EXTERNAL ONLY: If STEP 6 is True, proceed to STEP 11. Else proceed to STEP 8.
8. EXTERNAL ONLY: Send a side conversation to their Account Manager, providing the customer, key, website, and new expiration date, requesting approval.
9. EXTERNAL ONLY: Send a message to the requester explaining we contacted their Account Manager for approval and will get back to them as soon as a reply is received. We set the ticket on-hold and finish the process for now.
10. EXTERNAL ONLY: If the customer requests an update, we ask for one from the Account Manager and send the requester an acknowledgement and set the ticket on-hold. We finish the process for now.
11. EXTERNAL ONLY: When the AM replies on the Side Conversation, check whether they approve.
  - If they approve, proceed to STEP 12.
  - If they do not approve, communicate this to the customer and ask them to contact their AM directly, providing the AM's contact information.
12. Extend the license to the requested date.
13. Inform the customer that the process has been completed, sharing their license information and new expiration date.
14. Close the ticket.`
        ]
      }
    },
  
    packageSpecialInstructions: {
      title: "Special Instructions",
      content: "Optionally, provide any additional considerations regarding how the package should be executed.",
      notes: [
        "Do not add any workflow steps here.",
        "Use clear and concise language."
      ]
    },
  
    packageApprovalNotes: {
      title: "Approval Notes",
      content: "Detail any approval criteria required to execute actions. These requirements will be reinforced for AI.",
      notes: [
        "Steps to approve and general criteria should also be mentioned in the workflow itself.",
        "Use clear and concise language."
      ],
      examples: {
        plainText: [
`A request can be considered approved if one of the following is true:
  (A) The request comes from an internal user.
  (B) The request has been approved by the Account Manager.`
        ]
      }
    },

    packageActiveSkills: {
      title: "Active Skills",
      content: "Choose the skills that can be used in this package during execution but do not require a specialized agent. Note that you should not add skills that are to be used by the additional agents selected in the next section.",
    },

    packageHelperAgents: {
      title: "Helper Agents",
      content: "Select the additional agents that should be part of this package's execution. Agents will be able to use any skills that they have had assigned to them and will behave according to their system prompt."
    }

  };
  
  export const adapterFormHelpText: Record<string, HelpTextConfig> = {
    adapterName: {
      title: "Adapter Name",
      content: "Enter a unique name for your adapter. Use only lowercase letters, numbers, and underscores.",
      examples: {
        good: ["my-adapter", "data-processor"],
        bad: ["My Adapter", "DATA_PROCESSOR"]
      }
    },
    adapterDescription: {
      title: "Adapter Description",
      content: "Provide a clear description of what your adapter does.",
      examples: {
        good: ["Processes data from external API", "Transforms input into required format"]
      }
    },
    adapterUrlEndpoint: {
      title: "URL Endpoint",
      content: "This URL endpoint is automatically generated from the Adapter Name.",
      examples: {
        good: ["https://<AgenticGenie-Marketplace>/adapter/my-adapter"]
      }
    },
    adapterAuthentication: {
      title: "Authentication",
      content: "Authentication credentials or token for your adapter service.",
      examples: {
        good: ["Bearer token", "API key"]
      }
    },
    adapterTags: {
      title: "Tags",
      content: "Add tags to help categorize and find your adapter.",
      examples: {
        good: ["data-processing", "api-integration"]
      }
    }
  };
  
  // Helper function to format help text content
  export const formatHelpText = (config: HelpTextConfig): string => {
    let formattedText = config.content + "\n\n";
  
    if (config.notes?.length) {
      formattedText += "Notes:\n";
      config.notes.forEach(req => {
        formattedText += `• ${req}\n`;
      });
      formattedText += "\n";
    }
  
    if (config.examples) {
      // Show plainText examples if they exist
      if (config.examples.plainText?.length) {
        if (config.examples.plainText.length === 1) {
          formattedText += "Example:\n";
        } else {
          formattedText += "Examples:\n";
        }
        
        config.examples.plainText.forEach((example, index) => {
          // Add spacing between multiple plain text examples
          if (index > 0) formattedText += "\n";
          formattedText += example + "\n";
        });
        formattedText += "\n";
      }
  
      // Show good/bad examples if they exist
      if (config.examples.good?.length || config.examples.bad?.length) {
        // Add a separator if we had plainText examples
        if (config.examples.plainText?.length) {
          formattedText += "Additional examples:\n";
        } else {
          formattedText += "Examples:\n";
        }
  
        if (config.examples.good?.length) {
          config.examples.good.forEach(example => {
            formattedText += `✓ ${example}\n`;
          });
        }
        
        if (config.examples.bad?.length) {
          formattedText += "\nAvoid:\n";
          config.examples.bad.forEach(example => {
            formattedText += `✗ ${example}\n`;
          });
        }
        formattedText += "\n";
      }
    }
  
    if (config.sections?.length) {
      config.sections.forEach(section => {
        formattedText += `${section.title}:\n`;
        if (section.content) {
          formattedText += `${section.content}\n`;
        }
        if (section.items?.length) {
          section.items.forEach(item => {
            formattedText += ` • ${item}\n`;
          });
        }
      });
    }
  
    return formattedText.trim();
  };